import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { Box, Typography, Button, IconButton } from '@material-ui/core';
import styled from 'styled-components';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  WorkOffRounded as GeneralLeaveIcon,
  CakeRounded as BirthdayIcon,
  BeachAccessRounded as PublicHolidayIcon,
  OpenInNewRounded as LaunchIcon,
  AspectRatioRounded as MaximizeIcon
} from '@material-ui/icons';

// eslint-disable-next-line import/no-cycle
import { pages } from 'pages';
import { Line, Row, Table, TitleContainer, DashboardCalendar } from 'components';
import {
  AuthContext,
  filterOrgEnabledPageEntries,
  getApplicationDisplayName,
  getApplicationIconUrl,
  getApplicationInitials,
  isOrgFeatureEnabled,
  useSpot
} from 'framework';
import { EventType, OrgApplication } from 'domain-types';

const Events = styled(Box)`
  flex: 1 1 460px;
  border-left: 1px solid #f0f0f0;
  padding-left: ${p => p.theme.spacing(3)}px;
  margin-left: ${p => p.theme.spacing(1)}px;
`;

const EventsHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing(2)}px;
  padding: ${p => p.theme.spacing(1, 0)};
`;

const EventsTitle = styled(Typography)`
  font-weight: 700;
  color: #1e293b;
`;

const MaximizeButton = styled(IconButton)`
  padding: 8px;
  color: #64748b;
  border: 1px solid #e2e8f0;
  background-color: #ffffff;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f1f5f9;
    border-color: #cbd5e1;
    color: #0f172a;
    transform: scale(1.05);
  }
`;

const Container = styled(Box)`
  flex: 1 1 660px;
  display: flex;
  flex-direction: row;
  max-height: 640px;
  overflow: auto;
`;

const StyledButton = styled(Button)`
  margin: ${p => p.theme.spacing(2)}px;
  ${p => p.theme.breakpoints.down('sm')} {
    width: 130px;
    height: 130px;
  }
  ${p => p.theme.breakpoints.up('md')} {
    width: 192px;
    height: 192px;
  }

  padding: ${p => p.theme.spacing(2)}px;

  .MuiButton-label {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`;

const ButtonTitle = styled(Typography)`
  ${p => p.theme.breakpoints.down('sm')} {
    font-size: 0.9em;
  }
`;

const Spacer = styled(Box)`
  margin-top: ${p => p.theme.spacing(2)}px;
`;

const AppsSection = styled(Box)`
  margin-top: ${p => p.theme.spacing(2)}px;
`;

const AppsGrid = styled(Box)`
  display: flex;
  flex-wrap: wrap;
`;

const AppLink = styled.a`
  color: inherit;
  display: block;
  margin: ${p => p.theme.spacing(2)}px;
  text-decoration: none;
`;

const AppTile = styled(Box)`
  align-items: center;
  border: 1px solid ${p => p.theme.palette.divider};
  border-radius: 4px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${p => p.theme.spacing(2)}px;
  position: relative;
  text-align: center;

  ${p => p.theme.breakpoints.down('sm')} {
    height: 130px;
    width: 130px;
  }

  ${p => p.theme.breakpoints.up('md')} {
    height: 192px;
    width: 192px;
  }

  &:hover {
    border-color: ${p => p.theme.palette.text.primary};
  }
`;

const AppButtonContent = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
`;

const AppIconFallback = styled(Box)`
  align-items: center;
  background: ${p => p.theme.palette.grey[200]};
  border-radius: 12px;
  display: flex;
  font-weight: 600;
  height: 48px;
  justify-content: center;
  margin-bottom: ${p => p.theme.spacing(2)}px;
  text-transform: uppercase;
  width: 48px;
`;

const AppIconImage = styled.img`
  border-radius: 12px;
  height: 48px;
  margin-bottom: ${p => p.theme.spacing(2)}px;
  object-fit: contain;
  width: 48px;
`;

const AppText = styled(Box)`
  min-width: 0;
  width: 100%;
`;

const AppLaunchIcon = styled(LaunchIcon)`
  position: absolute;
  right: ${p => p.theme.spacing(1.5)}px;
  top: ${p => p.theme.spacing(1.5)}px;
  color: ${p => p.theme.palette.text.secondary};
`;

export const getVisibleApplications = (
  applications: OrgApplication[] = [],
  applicationIds: string[] = []
) =>
  applications.filter(
    application =>
      application.visibility === 'all_users' ||
      applicationIds.includes(application.id)
  );

function DashboardAppIcon({ application }: { application: OrgApplication }) {
  const [imgError, setImgError] = useState(false);
  const iconUrl = getApplicationIconUrl(application);

  if (!iconUrl || imgError) {
    return (
      <AppIconFallback>{getApplicationInitials(application)}</AppIconFallback>
    );
  }

  return (
    <AppIconImage alt="" src={iconUrl} onError={() => setImgError(true)} />
  );
}

export function Dashboard() {
  const history = useHistory();
  const { t } = useTranslation();
  const { organization } = useParams<{ organization: string }>();
  const [calendarMaximized, setCalendarMaximized] = useState(false);
  const { isInitialized, isSignedIn, getCurrentUser, getGroups } = useContext(
    AuthContext
  );

  const { data, query } = useSpot();
  const currentUserId = getCurrentUser();

  useEffect(() => {
    (async () => {
      await query(`org/${organization}`, {}, ['organizations', organization]);
      await query(`org/${organization}/overview`, {}, [
        'organizations',
        organization,
        'overview'
      ]);
      if (currentUserId) {
        await query(`org/${organization}/user/${currentUserId}`, {}, [
          'users',
          currentUserId
        ]);
      }
    })();
  }, [currentUserId, organization, query]);

  const org = data?.organizations?.[organization];
  const { applications, name, overview } = org ?? {};
  const currentUser = currentUserId ? data?.users?.[currentUserId] : undefined;
  const appsEnabled = isOrgFeatureEnabled(org, 'apps');
  const leaveEnabled = isOrgFeatureEnabled(org, 'leave');
  const visibleApplications = getVisibleApplications(
    applications ?? [],
    currentUser?.applicationIds ?? []
  );
  const visiblePageEntries = filterOrgEnabledPageEntries(
    Object.entries(pages),
    org
  );
  const visibleEvents = (overview?.events ?? []).filter(event =>
    leaveEnabled ? true : event.type === 'birthday'
  );

  const getTypeIcon = (type: EventType) => {
    switch (type) {
      case 'birthday':
        return <BirthdayIcon />;
      case 'leave':
        return <GeneralLeaveIcon />;
      case 'public-holiday':
        return <PublicHolidayIcon />;
      default:
        return <GeneralLeaveIcon />;
    }
  };

  return (
    <>
      <TitleContainer>
        <Typography variant="h5">{name}</Typography>
      </TitleContainer>
      <Line />
      {calendarMaximized ? (
        <DashboardCalendar
          events={visibleEvents}
          onMinimize={() => setCalendarMaximized(false)}
        />
      ) : (
        <Row>
          <Container>
            <Box>
              {visiblePageEntries
                .map(([, innerPage]) => innerPage)
                .filter(innerPage => !innerPage.hidden)
                .filter(innerPage => {
                  if (!innerPage.authorized) {
                    return true;
                  }

                  if (!isInitialized() || !isSignedIn()) {
                    return false;
                  }

                  const userGroups = isInitialized() ? getGroups() : [];
                  return innerPage?.groups?.length
                    ? innerPage.groups?.some(group => userGroups.includes(group))
                    : true;
                })
                .map(page => {
                  const Icon = page.icon;
                  return (
                    <StyledButton
                      key={page.title}
                      variant="outlined"
                      onClick={() =>
                        history.push(
                          page.path
                            .replaceAll(':organization', organization)
                            .replaceAll(':userId', '')
                            .replaceAll(':tab', '')
                        )
                      }
                    >
                      <Icon fontSize="large" />
                      <Spacer />
                      <ButtonTitle variant="subtitle1" align="center">
                        {page.title ? t(page.title) : name}
                      </ButtonTitle>
                    </StyledButton>
                  );
                })}
              {appsEnabled && visibleApplications.length ? (
                <AppsSection>
                  <Typography variant="h6">{t('applications')}</Typography>
                  <AppsGrid>
                    {visibleApplications.map(application => (
                      <AppLink
                        key={application.id}
                        href={application.url}
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <AppTile>
                          <AppLaunchIcon fontSize="small" />
                          <AppButtonContent>
                            <DashboardAppIcon application={application} />
                            <AppText>
                              <Typography variant="subtitle1">
                                {getApplicationDisplayName(application)}
                              </Typography>
                            </AppText>
                          </AppButtonContent>
                        </AppTile>
                      </AppLink>
                    ))}
                  </AppsGrid>
                </AppsSection>
              ) : null}
            </Box>
          </Container>
          <Events>
            <EventsHeader>
              <EventsTitle variant="h6">{t('events')}</EventsTitle>
              <MaximizeButton
                aria-label={t('maximize')}
                onClick={() => setCalendarMaximized(true)}
                size="small"
              >
                <MaximizeIcon fontSize="small" />
              </MaximizeButton>
            </EventsHeader>
            <Table
              maxHeight="600px"
              rows={visibleEvents.map(event => ({
                id: `${event.name}-${event.date}`,
                cells: [
                  getTypeIcon(event.type),
                  event.name,
                  `${moment(event.date).format('D MMMM')}${
                    event.end && event.end !== event.date
                      ? ` - ${moment(event.end).format('D MMMM')}`
                      : ''
                  }`
                ]
              }))}
            />
          </Events>
        </Row>
      )}
    </>
  );
}
